import { useMemo } from "react";
import {
  AnySessionPart,
  SessionStatus,
  SessionPartKind,
  SessionPartWithContext,
  ArtifactV1SessionPart,
} from "../session.types";
import { getWebClipFromSessionItems } from "../session.utils";
import { SessionPartContext } from "@/mds/components/sessions/session-parts/session-part.types";
import { SessionViewMode } from "../session.constants";

export enum SessionItemKind {
  Standard = "standard",
  ReasoningGroup = "reasoning-group",
}

export interface SessionItemContext {
  isExpanded: boolean;
}

export type SessionItemData = {
  part: AnySessionPart;
  context: SessionPartContext;
};

export interface SessionItem {
  id: string;
  kind: SessionItemKind;
  data: SessionItemData[];
  context: SessionItemContext;
}

export interface SessionRenderingContext {
  status: SessionStatus | null;
  sessionViewMode: SessionViewMode;
  config: {
    openLinksInNewTab: boolean;
    prohibitNativeCmdClick?: boolean;
  };
  // ID of the last reasoning group in the current list
  lastReasoningGroupId: string | null;
  // ID of the very last item in the list
  lastItemId: string | null;
  // ID of the last note item in the session (for special rendering)
  lastArtifactId: string | null;
}

// Find the first item that contains a webclip
export const usePreviewSessionItems = ({ sessionItems }: { sessionItems: SessionItem[] }) =>
  useMemo(() => {
    const firstWebClipItem = getWebClipFromSessionItems({ sessionItems });

    if (!firstWebClipItem) {
      return { previewSessionItems: [] };
    }

    return { previewSessionItems: [firstWebClipItem] };
  }, [sessionItems]);

/**
 * Finds the ID of the last note in the given session items
 */
export const getLastArtifactId = (sessionItems: SessionItem[]): string | null => {
  // Find all note artifact items
  const noteItems = sessionItems.filter(item => {
    if (item.kind !== SessionItemKind.Standard || !item.data) return false;

    const partWithContext = item.data[0] as SessionPartWithContext;

    return (
      partWithContext.part.kind === SessionPartKind.ArtifactV1 &&
      acceptableArtifactKindsForLastNote.includes(partWithContext.part.artifact_kind)
    );
  });

  if (noteItems.length === 0) return null;

  // Return the ID of the last note item
  const lastNoteItem = noteItems[noteItems.length - 1];
  return lastNoteItem.id;
};

/**
 * Determines if the given session item is the last note in the session
 */
export const isLastNoteInSession = ({
  sessionItem,
  lastArtifactId,
}: {
  sessionItem: SessionItem;
  lastArtifactId: string | null;
}): boolean => {
  return lastArtifactId !== null && sessionItem.id === lastArtifactId;
};

/**
 * Extracts the note artifact from a session item if it contains one
 */
export const getLastNoteArtifactFromItem = (
  sessionItem: SessionItem
): ArtifactV1SessionPart | null => {
  if (sessionItem.kind !== SessionItemKind.Standard || !sessionItem.data) {
    return null;
  }

  const partWithContext = sessionItem.data[0] as SessionPartWithContext;

  if (
    partWithContext.part.kind === SessionPartKind.ArtifactV1 &&
    acceptableArtifactKindsForLastNote.includes(partWithContext.part.artifact_kind)
  ) {
    return partWithContext.part as ArtifactV1SessionPart;
  }

  return null;
};

const acceptableArtifactKindsForLastNote = ["NOTE_CREATED_V1", "NOTE_UPDATED_V1"];
